/**
 * AuthContext multi-tenant helpers (Issue #49 / Task 15).
 *
 * Small pure-ish helpers that back the AuthContext changes for multi-tenant
 * membership. Kept in their own file so they can be unit-tested without
 * spinning up the React provider.
 *
 *   - parseActiveTenantIdFromIdToken(idToken)
 *     Reads `custom:activeTenantId` from a JWT idToken, falling back to
 *     `custom:tenantId` when the active claim is absent or the empty string
 *     (the backend emits '' when a user has no active memberships). This
 *     mirrors `resolveTenantClaim` on the backend (Task 7).
 *
 *   - applyTenantClaimsToUser(user, idToken)
 *     Returns a copy of the user object with `activeTenantId` set from the
 *     resolved claim, and `user.tenantId` aliased to the same value so the
 *     pre-#49 consumers still work during the transition window.
 *
 *   - fetchMembershipsForUser(api)
 *     Pulls `GET /me/memberships`, returns the `memberships` array. Always
 *     resolves — on error or unexpected shape, returns `[]` and logs a
 *     `console.warn` so the UI can render without a tenant switcher rather
 *     than hard-failing the entire session.
 */
import { decodeBase64Url } from '../utils/base64url'

/**
 * Decode the payload of a JWT without verifying the signature.
 * Returns `null` for any malformed input.
 *
 * Mirrors the inline payload decode in AuthContext.scheduleTokenRefresh so
 * we have a single, tested implementation.
 */
const decodeJwtPayload = (idToken) => {
  if (!idToken || typeof idToken !== 'string') return null
  const parts = idToken.split('.')
  if (parts.length < 2) return null
  try {
    const json = decodeBase64Url(parts[1])
    return JSON.parse(json)
  } catch {
    return null
  }
}

/**
 * Read the active tenant claim out of an idToken, with backend-matching
 * fallback semantics.
 *
 * @param {string|null|undefined} idToken
 * @returns {string|null}
 */
export const parseActiveTenantIdFromIdToken = (idToken) => {
  const claims = decodeJwtPayload(idToken)
  if (!claims) return null
  const active = claims['custom:activeTenantId']
  if (typeof active === 'string' && active.length > 0) return active
  const legacy = claims['custom:tenantId']
  if (typeof legacy === 'string' && legacy.length > 0) return legacy
  return null
}

/**
 * Merge the resolved tenant claim onto the user object. Returns a new
 * object — callers should treat the input as immutable.
 *
 * During the multi-tenant rollout `user.tenantId` is aliased to the
 * resolved `activeTenantId` so consumers that have not been migrated still
 * read the correct value with zero behaviour change.
 *
 * @param {object|null} user
 * @param {string|null|undefined} idToken
 * @returns {object|null}
 */
export const applyTenantClaimsToUser = (user, idToken) => {
  if (!user) return null
  if (!idToken) {
    // No fresh claim available — keep existing tenantId, mirror it as the
    // resolved active tenant.
    return {
      ...user,
      activeTenantId: user.activeTenantId ?? user.tenantId ?? null,
    }
  }
  const resolved = parseActiveTenantIdFromIdToken(idToken)
  return {
    ...user,
    activeTenantId: resolved,
    tenantId: resolved,
  }
}

/**
 * Fetch the calling user's active memberships. Returns an array (possibly
 * empty); never throws.
 *
 * The empty-on-failure contract is intentional: a failed membership lookup
 * must not knock the user out of the session — they have a valid token, just
 * no switcher. The console.warn keeps the failure visible in dev tools.
 *
 * @param {{ get: (url: string) => Promise<{data: any}> }} api
 * @returns {Promise<Array>}
 */
export const fetchMembershipsForUser = async (api) => {
  try {
    const response = await api.get('/me/memberships')
    const memberships = response?.data?.memberships
    return Array.isArray(memberships) ? memberships : []
  } catch (err) {
    console.warn('Failed to fetch /me/memberships:', err?.message || err)
    return []
  }
}
